import { Suspense, useEffect, useRef, useState, type ReactNode } from 'react';
import { Outlet, Link as RouterLink, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Recycle,
  Building2,
  Users,
  LogOut,
  Menu,
  Settings,
  Sun,
  Moon,
  User as UserIcon,
  Building,
  PackageOpen,
  ArrowUpFromLine,
  Wrench,
  Boxes,
  FileText,
  BarChart2,
  HelpCircle,
  LifeBuoy,
  MessageSquare,
  Megaphone,
  Search,
  Activity,
  CalendarClock,
  Wallet,
  Receipt,
  Archive,
  TrendingUp,
  Handshake,
  UserCog,
  Check,
  Loader2,
  Gift,
  ChevronDown,
} from 'lucide-react';

import { useAdminTicketUnreadCount } from '@/modules/admin/hooks/use-admin-support';
import { useUnseenPickupCount } from '@/modules/pickup-schedule/hooks/use-pickup-orders';

import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/components/ui/button';
import { BillingBanner } from '@/shared/components/BillingBanner';
import { TrialBanner } from '@/modules/signup/components/TrialBanner';
import { LanguageSwitcher } from '@/shared/components/LanguageSwitcher';
import { ImpersonationBanner } from '@/shared/components/ImpersonationBanner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/shared/components/ui/sheet';
import { Badge } from '@/shared/components/ui/badge';
import { useTheme } from '@/shared/hooks/useTheme';
import { useAuth } from '@/shared/auth/AuthContext';
import { useTenantFeatures, type TenantFeatures } from '@/shared/hooks/use-tenant-features';
import { useEntitlements } from '@/shared/hooks/use-entitlements';
import { useTenantProfile, type TenantEntitlements } from '@/modules/settings/tenant-profile/hooks/use-tenant-profile';
import { BrandMark } from '@/shared/components/BrandMark';

// Operator visibility toggles (operator-controlled, settings.features).
const FEATURE_ROUTES: Record<string, keyof TenantFeatures> = {
  '/catalog/companies': 'companies',
  '/catalog/persons': 'persons',
};

// Paid add-on modules (admin-controlled, entitlements.modules).
const ENTITLEMENT_ROUTES: Record<string, keyof TenantEntitlements['modules']> = {
  '/operations/treatment': 'treatment',
  '/operations/pickup-schedule': 'pickup',
  '/nexus': 'nexus',
  '/contabilitate': 'accounting',
  '/catalog/market-prices': 'market_prices',
  '/crm': 'crm',
  '/hr': 'hr',
};
import { useHints } from '@/shared/hooks/use-hints';
import { useCommandPalette } from '@/shared/hooks/use-command-palette';
import { CommandPalette } from '@/shared/components/CommandPalette';
import { NotificationsBell } from '@/shared/components/NotificationsBell';
import { SessionExpiredModal } from '@/shared/components/SessionExpiredModal';
import { AssistantChatWidget } from '@/modules/assistant/components/AssistantChatWidget';
import { CURRENT_VERSION } from '@/modules/support/pages/releases';
import { RouteLoader } from '@/shared/components/RouteLoader';
import { ErrorBoundary } from '@/shared/components/ErrorBoundary';
import { OfflineBanner } from '@/shared/components/OfflineBanner';

type IconType = typeof LayoutDashboard;

interface NavItem {
  to: string;
  labelKey: string;
  icon: IconType;
  end?: boolean;
  badge?: number;
  /** Other routes that light this item up (pages merged under one entry). */
  match?: string[];
}

interface NavSection {
  /** Section label key, or `null` for the leading no-header group. */
  labelKey: string | null;
  items: NavItem[];
  /** Pinned at the bottom of the sidebar, outside the scrolling list. */
  isFooter?: boolean;
}

// Groups named after what people do, not after modules (DESIGN.md §9). Routes
// are unchanged; merged pages (Facturare, Ajutor și suport) share one entry.
const OPERATOR_NAV: NavSection[] = [
  {
    labelKey: null,
    items: [{ to: '/', labelKey: 'nav.home', icon: LayoutDashboard, end: true }],
  },
  {
    labelKey: 'nav.section.operations',
    items: [
      { to: '/operations/collections', labelKey: 'nav.collections', icon: PackageOpen },
      { to: '/operations/outgoing', labelKey: 'nav.outgoing', icon: ArrowUpFromLine },
      { to: '/operations/treatment', labelKey: 'nav.treatment', icon: Wrench },
      { to: '/operations/stock', labelKey: 'nav.stock', icon: Boxes },
      { to: '/operations/pickup-schedule', labelKey: 'nav.pickup_schedule', icon: CalendarClock },
      { to: '/containers', labelKey: 'nav.containers', icon: Archive },
    ],
  },
  {
    labelKey: 'nav.section.documents',
    items: [
      { to: '/operations/documents', labelKey: 'nav.documents', icon: FileText },
      { to: '/reports', labelKey: 'nav.reports', icon: BarChart2 },
    ],
  },
  {
    labelKey: 'nav.section.money',
    items: [
      { to: '/financials', labelKey: 'nav.financials', icon: Wallet },
      { to: '/contabilitate', labelKey: 'nav.billing', icon: Receipt, match: ['/contabilitate', '/nexus'] },
    ],
  },
  {
    labelKey: 'nav.section.partners',
    items: [
      { to: '/catalog/companies', labelKey: 'nav.companies', icon: Building2 },
      { to: '/catalog/persons', labelKey: 'nav.persons', icon: Users },
      { to: '/crm', labelKey: 'nav.crm', icon: Handshake },
    ],
  },
  {
    labelKey: 'nav.section.catalog',
    items: [
      { to: '/catalog/wastes', labelKey: 'nav.wastes', icon: Recycle },
      { to: '/catalog/market-prices', labelKey: 'nav.marketPrices', icon: TrendingUp },
    ],
  },
  {
    labelKey: null,
    items: [{ to: '/hr', labelKey: 'nav.hr', icon: UserCog }],
  },
  {
    labelKey: null,
    isFooter: true,
    items: [
      { to: '/settings', labelKey: 'nav.settings', icon: Settings },
      { to: '/help', labelKey: 'nav.helpSupport', icon: LifeBuoy, match: ['/help', '/support'] },
      { to: '/updates', labelKey: 'nav.updates', icon: Megaphone },
    ],
  },
];

const ADMIN_NAV: NavSection[] = [
  {
    labelKey: null,
    items: [
      { to: '/admin/tenants', labelKey: 'nav.tenants', icon: Building },
      { to: '/admin/managers', labelKey: 'nav.adminManagers', icon: Handshake },
      { to: '/admin/referrals', labelKey: 'nav.adminReferrals', icon: Gift },
      { to: '/admin/monitoring', labelKey: 'nav.adminMonitoring', icon: Activity },
      { to: '/admin/support', labelKey: 'nav.adminSupport', icon: MessageSquare },
    ],
  },
];

// Converts a hex color to the "H S% L%" triplet format used by the shadcn/Tailwind
// theme variables (e.g. `--primary: 217 91% 60%`), so tenant branding overrides the
// real theme tokens every component already consumes — not an unused shadow variable.
function hexToHslParts(hex: string): { hsl: string; l: number } {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  const d = max - min;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r: h = ((g - b) / d) % 6; break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h *= 60;
    if (h < 0) h += 360;
  }
  return { hsl: `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`, l };
}

function relativeLuminance(hex: string): number {
  const channel = (i: number): number => {
    const v = parseInt(hex.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}

function applyBrandColor(varName: 'primary' | 'secondary', hex: string | null): void {
  const root = document.documentElement;
  const fgVar = `--${varName}-foreground`;
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) {
    root.style.removeProperty(`--${varName}`);
    root.style.removeProperty(fgVar);
    if (varName === 'primary') root.style.removeProperty('--ring');
    return;
  }
  const { hsl } = hexToHslParts(hex);
  root.style.setProperty(`--${varName}`, hsl);
  // Text on the brand color: whichever of white / near-black contrasts more
  // (WCAG relative luminance), not a lightness guess.
  root.style.setProperty(fgVar, relativeLuminance(hex) > 0.179 ? '222 47% 11%' : '0 0% 100%');
  if (varName === 'primary') root.style.setProperty('--ring', hsl);
}

/**
 * Swaps the app-wide PWA manifest/meta tags to the admin console's own
 * install identity when the admin shell mounts, so "Add to Home Screen" on
 * /admin installs a separate app (own name/icon/scope) instead of failing
 * outright — the default manifest is scoped to /m/ (field app) and doesn't
 * cover /admin/. Admin is always redirected to /admin/* for the whole
 * session (see App.tsx RequireAuth), so a one-time swap on mount is enough.
 */
function AdminPwaEffect(): null {
  useEffect(() => {
    const manifestLink = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (manifestLink) manifestLink.href = '/admin/manifest.webmanifest';

    const appleTitle = document.querySelector<HTMLMetaElement>(
      'meta[name="apple-mobile-web-app-title"]',
    );
    if (appleTitle) appleTitle.content = 'VanaGreen Admin';

    const appleIcon = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
    if (appleIcon) appleIcon.href = '/logo.png';

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/admin/sw.js', { scope: '/admin/' }).catch(() => {
        /* SW registration is best-effort; the app works without it */
      });
    }
  }, []);

  return null;
}

function BrandingEffect(): null {
  const profile = useTenantProfile();
  const primary = profile.data?.data.primary_color ?? null;
  const secondary = profile.data?.data.secondary_color ?? null;

  useEffect(() => {
    applyBrandColor('primary', primary);
  }, [primary]);

  useEffect(() => {
    applyBrandColor('secondary', secondary);
  }, [secondary]);

  return null;
}

function VersionBadge(): JSX.Element {
  const { t } = useTranslation();
  return (
    <RouterLink
      to="/updates"
      title={t('nav.updates')}
      className="ml-auto shrink-0 rounded px-1.5 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
    >
      {CURRENT_VERSION}
    </RouterLink>
  );
}

function ThemeToggle(): JSX.Element {
  const { theme, toggle } = useTheme();
  const { t } = useTranslation();
  const isDark = theme === 'dark';
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggle}
      aria-label={t('common.theme')}
      title={isDark ? t('common.light') : t('common.dark')}
    >
      {isDark ? <Sun className="size-4" /> : <Moon className="size-4" />}
    </Button>
  );
}

/**
 * PLAN.md item 7 — visible only when the current session belongs to a
 * `manager` (managerContext present in /me). A dropdown, not a banner:
 * unlike admin impersonation this is the manager's normal day-to-day mode
 * of working, not a support/risk mode.
 */
function TenantSwitcher(): JSX.Element | null {
  const { t } = useTranslation();
  const { user, switchManagerTenant } = useAuth();
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  if (!user?.managerContext) return null;
  const { tenantId, tenantName, availableTenants } = user.managerContext;

  const handleSwitch = async (nextTenantId: string): Promise<void> => {
    if (nextTenantId === tenantId || switchingId) return;
    setSwitchingId(nextTenantId);
    try {
      await switchManagerTenant(nextTenantId);
    } finally {
      setSwitchingId(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="hidden gap-2 md:inline-flex"
          data-testid="tenant-switcher-trigger"
        >
          <Building2 className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
          <span className="max-w-[160px] truncate">{tenantName}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[220px]">
        <DropdownMenuLabel>{t('managerSwitcher.label')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {availableTenants.map((tenant) => (
          <DropdownMenuItem
            key={tenant.id}
            disabled={switchingId !== null}
            onSelect={() => void handleSwitch(tenant.id)}
            className="flex items-center gap-2"
          >
            {switchingId === tenant.id ? (
              <Loader2 className="size-3.5 shrink-0 animate-spin" aria-hidden />
            ) : (
              <Building2 className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
            )}
            <span className="flex-1 truncate">{tenant.name}</span>
            {tenant.id === tenantId ? (
              <Check className="size-3.5 shrink-0 text-primary" aria-hidden />
            ) : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserMenu(): JSX.Element | null {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  if (!user) return null;
  const initials = (() => {
    const local = user.email.split('@')[0] ?? '';
    if (!local) return '?';
    const parts = local.split(/[._-]/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase();
    }
    return local.slice(0, 2).toUpperCase();
  })();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('auth.userMenu.ariaLabel')}
          className="flex size-9 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary hover:bg-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {initials}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[240px]">
        <DropdownMenuLabel className="space-y-1">
          <div className="truncate text-sm font-medium" title={user.email}>
            {user.email}
          </div>
          <div className="text-xs text-muted-foreground">{user.role}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild className="flex items-center gap-2">
          <RouterLink to="/settings/security">
            <UserIcon className="size-4" aria-hidden />
            {t('auth.userMenu.security')}
          </RouterLink>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => void logout()}
          className="flex items-center gap-2 text-destructive focus:text-destructive"
        >
          <LogOut className="size-4" aria-hidden />
          {t('auth.userMenu.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function isItemActive(item: NavItem, pathname: string): boolean {
  const paths = item.match ?? [item.to];
  if (item.end) return pathname === item.to;
  return paths.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function SidebarItem({
  item,
  pathname,
  onNavigate,
}: {
  item: NavItem;
  pathname: string;
  onNavigate?: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const Icon = item.icon;
  const isActive = isItemActive(item, pathname);
  return (
    <RouterLink
      to={item.to}
      onClick={onNavigate}
      aria-current={isActive ? 'page' : undefined}
      // Selection = surface tint + stronger text, no accent stripe; hover sits
      // on the same axis, weaker (DESIGN.md §9).
      className={cn(
        'flex h-control items-center gap-3 rounded-md px-3 text-sm text-muted-foreground transition-colors duration-150',
        'hover:bg-accent/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isActive && 'bg-accent font-medium text-foreground hover:bg-accent',
      )}
    >
      <Icon className="size-4 shrink-0" aria-hidden />
      <span className="flex-1 truncate">{t(item.labelKey)}</span>
      {item.badge !== null && item.badge !== undefined && item.badge > 0 ? (
        <span
          className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-xs font-medium tabular-nums text-destructive-foreground"
          aria-label={t('nav.unread', { count: item.badge })}
        >
          {item.badge > 99 ? '99+' : item.badge}
        </span>
      ) : null}
    </RouterLink>
  );
}

const COLLAPSED_LS_KEY = 'vanagreen:nav_collapsed';

function readCollapsed(): string[] {
  try {
    const raw = localStorage.getItem(COLLAPSED_LS_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function SidebarBody({
  sections,
  onNavigate,
}: {
  sections: NavSection[];
  onNavigate?: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const navRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  // Folded groups are remembered per browser; the group of the page you're on
  // always stays open, so the current place is never hidden.
  const [collapsed, setCollapsed] = useState<string[]>(readCollapsed);

  const toggle = (key: string): void => {
    setCollapsed((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      try {
        localStorage.setItem(COLLAPSED_LS_KEY, JSON.stringify(next));
      } catch {
        /* private mode: folding just isn't remembered */
      }
      return next;
    });
  };

  // At 150% zoom the active item is often below the fold of the menu.
  useEffect(() => {
    navRef.current?.querySelector('[aria-current="page"]')?.scrollIntoView({ block: 'nearest' });
  }, [pathname]);

  const main = sections.filter((s) => !s.isFooter && s.items.length > 0);
  const footer = sections.filter((s) => s.isFooter).flatMap((s) => s.items);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <nav
        ref={navRef}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-2"
        aria-label={t('a11y.nav.primary')}
      >
        {main.map((section, idx) => {
          const key = section.labelKey ?? `s-${idx}`;
          const hasActive = section.items.some((item) => isItemActive(item, pathname));
          const isOpen = !section.labelKey || hasActive || !collapsed.includes(key);
          return (
            <div key={key} className="mb-2">
              {section.labelKey ? (
                <button
                  type="button"
                  // A group heading that folds: native button on purpose, it's
                  // the heading row, not an action.
                  onClick={() => toggle(key)}
                  aria-expanded={isOpen}
                  disabled={hasActive}
                  className="flex h-8 w-full items-center justify-between rounded-md px-3 text-xs text-muted-foreground hover:text-foreground disabled:cursor-default disabled:hover:text-muted-foreground"
                >
                  {t(section.labelKey)}
                  <ChevronDown className={cn('size-4', !isOpen && '-rotate-90')} aria-hidden />
                </button>
              ) : null}
              {isOpen ? (
                <div className="space-y-0.5">
                  {section.items.map((item) => (
                    <SidebarItem key={item.to} item={item} pathname={pathname} onNavigate={onNavigate} />
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </nav>
      <div className="shrink-0 space-y-0.5 border-t px-2 py-2">
        {footer.map((item) => (
          <SidebarItem key={item.to} item={item} pathname={pathname} onNavigate={onNavigate} />
        ))}
        <Button
          type="button"
          variant="ghost"
          className="w-full justify-start gap-3 px-3 font-normal text-muted-foreground hover:text-foreground"
          onClick={() => void logout()}
        >
          <LogOut className="size-4" aria-hidden />
          {t('nav.logout')}
        </Button>
      </div>
    </div>
  );
}

interface ShellProps {
  variant: 'operator' | 'admin';
  children?: ReactNode;
}

function Shell({ variant }: ShellProps): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { hintsEnabled, toggleHints } = useHints();
  const palette = useCommandPalette();
  const { sessionExpired } = useAuth();
  const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform);
  // Admin-only endpoint: an operator session would get a 403 on every poll.
  const unreadQuery = useAdminTicketUnreadCount(variant === 'admin');
  const adminUnreadCount = variant === 'admin' ? (unreadQuery.data?.count ?? 0) : 0;

  const features = useTenantFeatures();
  const entitlements = useEntitlements();
  const unseenPickupQuery = useUnseenPickupCount(variant === 'operator' && entitlements.modules.pickup);
  const unseenPickupCount = unseenPickupQuery.data ?? 0;

  const navBadges: Record<string, number> =
    variant === 'admin'
      ? { '/admin/support': adminUnreadCount }
      : { '/operations/pickup-schedule': unseenPickupCount };
  const rawSections = variant === 'operator' ? OPERATOR_NAV : ADMIN_NAV;
  const sectionsWithBadge: NavSection[] = rawSections.map((section) => ({
    ...section,
    items: section.items.map((item) => (navBadges[item.to] ? { ...item, badge: navBadges[item.to] } : item)),
  }));

  const sections = sectionsWithBadge.map((section) => ({
    ...section,
    items: section.items
      // Facturare opens on Contabilitate, or on Nexus when that's the only one bought.
      .map((item) =>
        item.to === '/contabilitate' && !entitlements.modules.accounting && entitlements.modules.nexus
          ? { ...item, to: '/nexus' }
          : item,
      )
      .filter((item) => {
      if (variant !== 'operator') return true;
      const featureKey = FEATURE_ROUTES[item.to];
      if (featureKey && features[featureKey] === false) return false;
      const moduleKey = ENTITLEMENT_ROUTES[item.to];
      if (moduleKey && !entitlements.modules[moduleKey]) return false;
      return true;
    }),
  }));
  const tenantName = user?.tenant?.name;
  const { pathname } = useLocation();

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-3 focus:z-50 focus:rounded-md focus:bg-background focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:ring-2 focus:ring-ring"
      >
        {t('a11y.skipToContent')}
      </a>
      <div className="flex min-h-[100dvh] w-full bg-background text-foreground">
        {variant === 'operator' ? <BrandingEffect /> : null}
        {variant === 'admin' ? <AdminPwaEffect /> : null}
        {/* Stays put while the page scrolls; the menu scrolls on its own
            (DESIGN.md §9). The document stays the scroll container. */}
        <aside className="sticky top-0 hidden h-[100dvh] w-60 shrink-0 flex-col self-start border-r bg-card lg:flex">
          <div className="flex h-14 shrink-0 items-center gap-2.5 border-b px-4">
            <BrandMark className="size-8" />
            <span className="text-sm font-semibold tracking-tight">
              {t('brand.short')}
            </span>
            <VersionBadge />
          </div>
          <SidebarBody sections={sections} />
        </aside>

        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetContent side="left" className="w-72 gap-0 p-0">
            <SheetHeader className="px-4 py-3">
              <SheetTitle className="flex items-center gap-2.5">
                <BrandMark className="size-8" />
                {t('brand.short')}
                <VersionBadge />
              </SheetTitle>
            </SheetHeader>
            <SidebarBody sections={sections} onNavigate={() => setDrawerOpen(false)} />
          </SheetContent>
        </Sheet>

        <div className="flex flex-1 flex-col min-w-0">
          <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-3 border-b bg-card px-4 md:px-6">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setDrawerOpen(true)}
                aria-label={t('a11y.sidebar.toggle')}
              >
                <Menu className="size-4" />
              </Button>
              <span className="lg:hidden text-sm font-semibold">{t('brand.short')}</span>
            </div>
            {/* Search trigger — visible on desktop, compact on mobile */}
            {variant === 'operator' ? (
              <button
                type="button"
                onClick={palette.open}
                aria-label={t('search.ariaLabel')}
                className="hidden h-control min-w-0 max-w-md flex-1 items-center gap-2 rounded-md border border-input bg-background px-3 text-sm text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:flex"
                data-testid="command-palette-trigger"
              >
                <Search className="size-4 shrink-0" aria-hidden />
                <span className="flex-1 truncate text-left">{t('search.placeholder')}</span>
                <kbd className="ml-auto hidden rounded border bg-muted px-1.5 py-0.5 font-mono text-xs lg:inline">
                  {isMac ? '⌘ K' : 'Ctrl K'}
                </kbd>
              </button>
            ) : null}
            <div className="flex items-center gap-1">
              {/* Mobile search icon */}
              {variant === 'operator' ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden"
                  onClick={palette.open}
                  aria-label={t('search.ariaLabel')}
                >
                  <Search className="size-4" />
                </Button>
              ) : null}
              {variant === 'admin' ? (
                <Badge variant="danger">
                  {t('admin.console')}
                </Badge>
              ) : tenantName ? (
                <Badge variant="neutral" className="hidden max-w-48 truncate xl:inline-flex">{tenantName}</Badge>
              ) : null}
              {/* Hints toggle */}
              {variant === 'operator' ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn('hidden sm:inline-flex', hintsEnabled && 'text-primary')}
                  onClick={toggleHints}
                  aria-label={t('hints.toggle.ariaLabel')}
                  title={hintsEnabled ? t('hints.toggle.disable') : t('hints.toggle.enable')}
                >
                  <HelpCircle className="size-4" />
                </Button>
              ) : null}
              {variant === 'operator' ? <NotificationsBell /> : null}
              {variant === 'operator' ? <TenantSwitcher /> : null}
              <span className="hidden sm:contents">
                <LanguageSwitcher />
              </span>
              <ThemeToggle />
              <UserMenu />
            </div>
          </header>

          {/* Command palette */}
          {variant === 'operator' ? (
            <CommandPalette open={palette.isOpen} onClose={palette.close} />
          ) : null}

          {/* Session expiry modal — shown over any page */}
          <SessionExpiredModal open={sessionExpired} />

          {variant === 'operator' ? <AssistantChatWidget /> : null}

          <main id="main-content" tabIndex={-1} className="flex-1 focus:outline-none">
            <OfflineBanner />
            <ImpersonationBanner />
            {variant === 'operator' ? <BillingBanner /> : null}
            {variant === 'operator' ? <TrialBanner /> : null}
            {/* pb-20 keeps the floating assistant button off the last row. */}
            <div className="mx-auto w-full max-w-screen-2xl px-4 pb-20 pt-4 md:px-8 md:pt-6 [@media(max-height:700px)]:pt-4">
              <ErrorBoundary resetKey={pathname}>
                <Suspense fallback={<RouteLoader />}>
                  <Outlet />
                </Suspense>
              </ErrorBoundary>
            </div>
          </main>
          <footer className="border-t bg-card px-4 py-3 md:px-6">
            <div className="mx-auto flex w-full max-w-screen-2xl items-center justify-between gap-4">
              <p className="text-xs text-muted-foreground">{t('footer.copyright')}</p>
              <RouterLink
                to="/privacy-policy"
                className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              >
                {t('footer.privacy')}
              </RouterLink>
            </div>
          </footer>
        </div>
      </div>
    </>
  );
}

export function AppLayout(): JSX.Element {
  return <Shell variant="operator" />;
}

export function AdminLayout(): JSX.Element {
  return <Shell variant="admin" />;
}
